/**
 * Centralized API utility for consistent fetch handling
 * - Handles online/offline detection
 * - Automatic error handling for common HTTP status codes
 * - Consistent credentials and headers
 */

import { API_URL } from "../config/api";
import { getAuthHeaders } from "./authHeaders";
import { refreshAccessToken } from "./tokenStore";

export interface ApiError {
  status: number;
  message: string;
  isOffline: boolean;
  isUnauthorized: boolean;
}

export class ApiException extends Error {
  status: number;
  isOffline: boolean;
  isUnauthorized: boolean;

  constructor(error: ApiError) {
    super(error.message);
    this.status = error.status;
    this.isOffline = error.isOffline;
    this.isUnauthorized = error.isUnauthorized;
    this.name = 'ApiException';
  }
}

/**
 * Centralized fetch wrapper with error handling
 */
export const api = {
  /**
   * Base fetch method with all error handling
   */
  fetch: async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
    // Check online status before making request
    if (!navigator.onLine) {
      throw new ApiException({
        status: 0,
        message: "You are offline. Please check your internet connection.",
        isOffline: true,
        isUnauthorized: false,
      });
    }

    const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

    /* The Authorization header is sent as well as the cookie. Cookies are the
       primary credential, but the API is on another domain and Safari drops
       cross-site cookies, so header-only is the path those users travel. */
    const send = () =>
      fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
          ...options.headers,
        },
      });

    try {
      let response = await send();

      /* A 401 is the normal end of a fifteen-minute access token, not a
         signed-out user. Renew once and replay before believing it.

         The refresh endpoint itself is excluded, or a dead session would
         recurse: 401 -> refresh -> 401 -> refresh. */
      if (response.status === 401 && !endpoint.includes('/auth/refresh')) {
        const renewed = await refreshAccessToken();
        if (renewed) response = await send();
      }

      // Handle common error statuses
      if (!response.ok) {
        // 401 Unauthorized - dispatch event for global handling
        if (response.status === 401) {
          window.dispatchEvent(new CustomEvent('api:unauthorized'));
          throw new ApiException({
            status: 401,
            message: "Your session has expired. Please log in again.",
            isOffline: false,
            isUnauthorized: true,
          });
        }

        // 403 Forbidden
        if (response.status === 403) {
          throw new ApiException({
            status: 403,
            message: "You don't have permission to perform this action.",
            isOffline: false,
            isUnauthorized: false,
          });
        }

        // 404 Not Found
        if (response.status === 404) {
          throw new ApiException({
            status: 404,
            message: "The requested resource was not found.",
            isOffline: false,
            isUnauthorized: false,
          });
        }

        // 5xx Server Errors
        if (response.status >= 500) {
          throw new ApiException({
            status: response.status,
            message: "Server error. Please try again later.",
            isOffline: false,
            isUnauthorized: false,
          });
        }

        // Try to extract error message from response
        try {
          const errorData = await response.json();
          throw new ApiException({
            status: response.status,
            message: errorData.message || `Request failed with status ${response.status}`,
            isOffline: false,
            isUnauthorized: false,
          });
        } catch (e) {
          if (e instanceof ApiException) throw e;
          throw new ApiException({
            status: response.status,
            message: `Request failed with status ${response.status}`,
            isOffline: false,
            isUnauthorized: false,
          });
        }
      }

      return response;
    } catch (error) {
      // Re-throw ApiException
      if (error instanceof ApiException) {
        throw error;
      }

      // Network errors (fetch failed completely)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiException({
          status: 0,
          message: "Unable to connect to server. Please check your connection.",
          isOffline: !navigator.onLine,
          isUnauthorized: false,
        });
      }

      // Unknown error
      throw new ApiException({
        status: 0,
        message: error instanceof Error ? error.message : "An unexpected error occurred",
        isOffline: false,
        isUnauthorized: false,
      });
    }
  },

  /**
   * GET request
   */
  get: (endpoint: string, options?: RequestInit) =>
    api.fetch(endpoint, { ...options, method: 'GET' }),

  /**
   * POST request with JSON body
   */
  post: (endpoint: string, body?: any, options?: RequestInit) =>
    api.fetch(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  /**
   * PUT request with JSON body
   */
  put: (endpoint: string, body?: any, options?: RequestInit) =>
    api.fetch(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  /**
   * DELETE request with optional JSON body
   */
  delete: (endpoint: string, body?: any, options?: RequestInit) =>
    api.fetch(endpoint, {
      ...options,
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    }),
};

export default api;
